import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCampaign,
  getCampaignStats,
  startCampaign,
  pauseCampaign,
  stopCampaign,
  getCampaignCallStats,
  CampaignStatus,
  CampaignScheduleConfig,
  LiveDataParams,
  updateCampaignSchedule,
  exportCampaignAnalytics,
} from "../../services/api";
import {
  HiOutlinePlay,
  HiOutlinePause,
  HiOutlineStop,
  HiOutlineRefresh,
  HiChevronLeft,
  HiPhone,
  HiClock,
  HiTrendingUp,
  HiUsers,
  HiChartBar,
  HiCog,
  HiDownload,
} from "react-icons/hi";
import LoadingState from "../Common/LoadingState";
import ConfirmationModal from "../Common/ConfirmationModal";
import { Modal } from "../Common/Modal";
import ScheduleConfigModal from "../Campaigns/ScheduleConfigModal";
import { ContactsWithCalls } from "./ContactsWithCalls";
import CampaignAnalytics from "./CampaignAnalytics";
import LiveDataTable from "./LiveDataTable";
import { useToast } from "../Common/Toast";
import { formatIstDateTime } from "../../utils/dateTime";
import "./Admin.css";

const CAMPAIGN_RETURN_STACK_KEY = "campaign-list-return-stack";

const CampaignDetails: React.FC<{ isAdmin?: boolean }> = ({ isAdmin }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [showPauseModal, setShowPauseModal] = useState<boolean>(false);
  const [showStopModal, setShowStopModal] = useState<boolean>(false);
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false);
  const [showExportEmailModal, setShowExportEmailModal] = useState<boolean>(false);
  const [exportEmailInput, setExportEmailInput] = useState<string>("");
  const [activeTab, setActiveTab] = useState<'details' | 'analytics' | 'liveData'>('details');
  const [liveDataViewParams, setLiveDataViewParams] = useState<LiveDataParams>({});
  const sourceListPath =
    (location.state as { fromCampaignList?: string } | null)?.fromCampaignList ||
    "";

  const popStoredReturnPath = (): string | null => {
    try {
      const storedStack = sessionStorage.getItem(CAMPAIGN_RETURN_STACK_KEY);
      if (!storedStack) return null;

      const parsedStack = JSON.parse(storedStack);
      if (!Array.isArray(parsedStack) || parsedStack.length === 0) return null;

      const stack = parsedStack.filter((value): value is string => typeof value === "string");
      const previousUrl = stack.pop() || null;
      sessionStorage.setItem(CAMPAIGN_RETURN_STACK_KEY, JSON.stringify(stack));
      return previousUrl;
    } catch {
      return null;
    }
  };

  const handleBack = () => {
    const stackedPath = popStoredReturnPath();
    if (stackedPath) {
      navigate(stackedPath, { replace: true });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    if (sourceListPath) {
      navigate(sourceListPath, { replace: true });
      return;
    }

    navigate(isAdmin ? "/admin/campaigns" : "/campaigns");
  };

  const loggedInIdentity = useMemo(() => {
    const storedEmail = localStorage.getItem("email")?.trim();
    if (storedEmail) {
      return storedEmail;
    }

    const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
    if (!token) {
      return "";
    }

    try {
      const parts = token.split(".");
      if (parts.length < 2) {
        return "";
      }

      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      return (payload?.email || payload?.username || "").toString().trim();
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    if (!showExportEmailModal || !loggedInIdentity) {
      return;
    }
    setExportEmailInput((currentValue) => currentValue || loggedInIdentity);
  }, [showExportEmailModal, loggedInIdentity]);

  // Query for campaign data
  const { data: campaign, isLoading: campaignLoading, error: campaignError } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => getCampaign(id!),
    enabled: !!id,
  });

  // Query for campaign stats
  const { data: stats } = useQuery({
    queryKey: ['campaignStats', id],
    queryFn: () => getCampaignStats(id!),
    enabled: !!id,
    refetchInterval: campaign?.status === CampaignStatus.RUNNING ? 5 * 60 * 1000 : false, // Refresh every 5 minutes if running
  });

  // Query for call stats
  const { data: callStats } = useQuery({
    queryKey: ['campaignCallStats', id],
    queryFn: () => getCampaignCallStats(id!),
    enabled: !!id,
    refetchInterval: campaign?.status === CampaignStatus.RUNNING ? 5 * 60 * 1000 : false, // Refresh every 5 minutes if running
  });

  // Mutation for starting campaign
  const startMutation = useMutation({
    mutationFn: () => startCampaign(id!),
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['campaign', id] });
        queryClient.invalidateQueries({ queryKey: ['campaignStats', id] });
        queryClient.invalidateQueries({ queryKey: ['campaignCallStats', id] });
        showToast("Campaign started successfully", "success");
      } else {
        showToast(result.error || "Failed to start campaign", "error");
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || "Failed to start campaign";
      showToast(errorMessage, "error");
    },
  });

  // Mutation for pausing campaign
  const pauseMutation = useMutation({
    mutationFn: () => pauseCampaign(id!),
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['campaign', id] });
        queryClient.invalidateQueries({ queryKey: ['campaignStats', id] });
        queryClient.invalidateQueries({ queryKey: ['campaignCallStats', id] });
        setShowPauseModal(false);
        showToast("Campaign paused successfully", "success");
      } else {
        showToast(result.error || "Failed to pause campaign", "error");
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || "Failed to pause campaign";
      showToast(errorMessage, "error");
      setShowPauseModal(false);
    },
  });

  // Mutation for stopping campaign
  const stopMutation = useMutation({
    mutationFn: () => stopCampaign(id!),
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['campaign', id] });
        queryClient.invalidateQueries({ queryKey: ['campaignStats', id] });
        queryClient.invalidateQueries({ queryKey: ['campaignCallStats', id] });
        setShowStopModal(false);
        showToast("Campaign stopped successfully", "success");
      } else {
        showToast(result.error || "Failed to stop campaign", "error");
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || "Failed to stop campaign";
      showToast(errorMessage, "error");
      setShowStopModal(false);
    },
  });

  // Mutation for updating schedule
  const scheduleUpdateMutation = useMutation({
    mutationFn: (config: CampaignScheduleConfig) => updateCampaignSchedule(id!, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      setShowScheduleModal(false);
      showToast("Campaign schedule updated successfully", "success");
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || "Failed to update campaign schedule";
      showToast(errorMessage, "error");
      setShowScheduleModal(false);
    },
  });

  // Mutation for exporting campaign analytics
  const exportMutation = useMutation({
    mutationFn: (emailOverride?: string) =>
      exportCampaignAnalytics(id!, {
        search: liveDataViewParams.search,
        dateFrom: liveDataViewParams.dateFrom,
        dateTo: liveDataViewParams.dateTo,
        minDuration: liveDataViewParams.minDuration,
        maxDuration: liveDataViewParams.maxDuration,
        callStatus: liveDataViewParams.callStatus,
        outcome: liveDataViewParams.outcome,
        hideBlankMetrics: liveDataViewParams.hideBlankMetrics,
        email: emailOverride || undefined,
      }),
    onSuccess: (result) => {
      if (result.success) {
        setShowExportEmailModal(false);
        setExportEmailInput("");
        showToast(result.message, "success");
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || "Failed to export campaign analytics";
      showToast(errorMessage, "error");
    },
  });

  const handleScheduleUpdate = async (config: CampaignScheduleConfig) => {
    await scheduleUpdateMutation.mutateAsync(config);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['campaignStats', id] });
    queryClient.invalidateQueries({ queryKey: ['campaignCallStats', id] });
  };

  const handleExportWithEmail = async () => {
    await exportMutation.mutateAsync(exportEmailInput.trim() || undefined);
  };

  const getStatusBadge = (status: CampaignStatus) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case CampaignStatus.DRAFT:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case CampaignStatus.RUNNING:
        return `${baseClasses} bg-green-100 text-green-800`;
      case CampaignStatus.PAUSED:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case CampaignStatus.COMPLETED:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case CampaignStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const isActionLoading = startMutation.isPending || pauseMutation.isPending || stopMutation.isPending;

  if (campaignLoading) {
    return <LoadingState message="Loading campaign details..." />;
  }

  if (campaignError || !campaign) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Campaign not found</p>
          <button
            onClick={handleBack}
            className="mt-4 text-primary hover:text-primary/80 font-medium"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  const avgDuration = Math.round(callStats?.average_call_duration || 0)

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900"
          >
            <HiChevronLeft className="h-6 w-6" />
          </button>
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-[var(--foreground)]">{campaign.name}</h1>
              <span className={getStatusBadge(campaign.status)}>
                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
              </span>

            </div>
            <div className="flex items-center space-x-4 text-sm text-[var(--foreground-muted)]">
              <span className="flex items-center">
                <HiUsers className="h-4 w-4 mr-1" />
                {stats?.total_contacts || campaign.total_contacts || 0} contacts
              </span>
              <span className="flex items-center">
                <HiClock className="h-4 w-4 mr-1" />
                Created {formatIstDateTime(campaign.created_at, {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  second: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Download Report Button - Available for all users */}
          <button
            onClick={() => {
              setExportEmailInput(loggedInIdentity || "");
              setShowExportEmailModal(true);
            }}
            disabled={exportMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            title="Download campaign analytics report"
          >
            <HiDownload className="h-5 w-5" />
            {exportMutation.isPending ? "Exporting..." : "Download Report"}
          </button>

          {/* Campaign Control Buttons - Available for all users (admins and leads) */}
          {campaign.status === CampaignStatus.DRAFT && (
            <button
              onClick={() => startMutation.mutate()}
              disabled={isActionLoading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <HiOutlinePlay className="h-5 w-5" />
              Start
            </button>
          )}
          {campaign.status === CampaignStatus.RUNNING && (
            <>
              <button
                onClick={() => setShowPauseModal(true)}
                disabled={isActionLoading}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <HiOutlinePause className="h-5 w-5" />
                Pause
              </button>
            </>
          )}
          {campaign.status === CampaignStatus.PAUSED && (
            <button
              onClick={() => startMutation.mutate()}
              disabled={isActionLoading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <HiOutlinePlay className="h-5 w-5" />
              Resume
            </button>
          )}
          <button
            onClick={() => setShowScheduleModal(true)}
            disabled={campaign.status !== CampaignStatus.PAUSED}
            className={`${campaign.status === CampaignStatus.PAUSED
              ? 'bg-gray-600 hover:bg-gray-700'
              : 'bg-gray-400 cursor-not-allowed'
              } text-white px-4 py-2 rounded-lg flex items-center gap-2`}
            title={campaign.status !== CampaignStatus.PAUSED ? 'Schedule can only be edited when campaign is paused' : ''}
          >
            <HiCog className="h-5 w-5" />
            Edit Schedule
          </button>
          <button
            onClick={() => setShowStopModal(true)}
            disabled={isActionLoading || campaign.status === CampaignStatus.COMPLETED || campaign.status === CampaignStatus.FAILED}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <HiOutlineStop className="h-5 w-5" />
            Stop
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-[var(--border)] mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('details')}
            className={`${activeTab === 'details'
              ? 'border-[var(--primary)] text-[var(--primary)]'
              : 'border-transparent text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`${activeTab === 'analytics'
              ? 'border-[var(--primary)] text-[var(--primary)]'
              : 'border-transparent text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('liveData')}
            className={`${activeTab === 'liveData'
              ? 'border-[var(--primary)] text-[var(--primary)]'
              : 'border-transparent text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Live Data
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' ? (
        <div>
          {/* Calling Status Banner */}
          {campaign.status === CampaignStatus.RUNNING && callStats && (
            <div className={`mb-6 p-4 rounded-lg border ${callStats.calls_in_progress > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`h-3 w-3 rounded-full ${callStats.calls_in_progress > 0 ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                  <div>
                    <h3 className={`text-lg font-medium ${callStats.calls_in_progress > 0 ? 'text-green-900' : 'text-gray-900'}`}>
                      {callStats.calls_in_progress > 0 ? 'Calling in Progress' : 'Campaign Idle'}
                    </h3>
                    <p className={`text-sm ${callStats.calls_in_progress > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                      {callStats.calls_in_progress} active calls, {callStats.queue_depth} in queue
                    </p>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  {campaign.settings?.timezone && (
                    <p className={`text-sm ${callStats.calls_in_progress > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                      Timezone: {campaign.settings.timezone}
                    </p>
                  )}
                  {campaign.settings?.calling_hours?.start_time && campaign.settings?.calling_hours?.end_time && (
                    <p className={`text-sm ${callStats.calls_in_progress > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      Hours: {campaign.settings.calling_hours.start_time} - {campaign.settings.calling_hours.end_time}
                    </p>
                  )}
                  {campaign.settings?.active_days && campaign.settings.active_days.length > 0 && (
                    <p className={`text-sm ${callStats.calls_in_progress > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      Days: {campaign.settings.active_days.join(', ')}
                    </p>
                  )}
                  {campaign.settings?.retry_settings?.enabled && (
                    <p className={`text-sm ${callStats.calls_in_progress > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      Max attempts: {campaign.settings.retry_settings.max_attempts} every {Math.round(campaign.settings.retry_settings.retry_interval_hours * 60)} min
                    </p>
                  )}

                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          {stats && callStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">TOTAL MINUTES</p>
                    <p className="text-3xl font-bold text-[var(--foreground)]">{Math.round(callStats.total_minutes_consumed)}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {avgDuration > 59 ? `${Math.floor(avgDuration / 60)}m ${avgDuration % 60}s` : `${avgDuration}s`} avg duration
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <HiClock className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">SUCCESS RATE</p>
                    <p className="text-3xl font-bold text-[var(--foreground)]">{Math.round(callStats.success_rate)}%</p>
                  </div>
                  <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                    <HiTrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">ACTIVE CALLS</p>
                    <p className="text-3xl font-bold text-[var(--foreground)]">{callStats.calls_in_progress}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      of {campaign.settings?.call_limits?.max_concurrent || 25} max
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <HiPhone className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </div>

              <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">TOTAL CALLS TODAY</p>
                    <p className="text-3xl font-bold text-[var(--foreground)]">
                      {campaign.daily_calls_made || 0}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {campaign.settings?.call_limits?.max_per_day
                        ? `of ${campaign.settings.call_limits.max_per_day} daily limit`
                        : 'No daily limit set'}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <HiChartBar className="h-6 w-6 text-accent" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {stats && (
            <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)] mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[var(--foreground)]">Campaign Progress</h3>
                <button
                  onClick={handleRefresh}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <HiOutlineRefresh className="h-5 w-5" />
                </button>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div
                  className="bg-accent h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${stats.total_contacts > 0 ? (stats.processed_contacts / stats.total_contacts) * 100 : 0}%`,
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>{stats.processed_contacts || 0} of {stats.total_contacts || 0} contacts processed</span>
                <span>
                  {stats.total_contacts > 0
                    ? Math.round((stats.processed_contacts / stats.total_contacts) * 100)
                    : 0
                  }% complete
                </span>
              </div>
            </div>
          )}

          {/* Call Outcome Distribution */}
          {/* {callStats && Object.keys(callStats.outcome_distribution).length > 0 && (
        <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)] mb-8">
          <h3 className="text-lg font-semibold text-[var(--foreground)] mb-4">Call Outcomes</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(callStats.outcome_distribution).map(([outcome, count]) => {
              const values = Object.values(callStats.outcome_distribution) as number[];
              const total = values.reduce((a, b) => a + b, 0);
              const percentage = ((count / total) * 100).toFixed(1);
              
              let icon, colorClass;
              switch (outcome) {
                case 'answered':
                  icon = <HiCheckCircle className="h-5 w-5" />;
                  colorClass = 'text-green-600 bg-green-100';
                  break;
                case 'voicemail':
                  icon = <HiPhone className="h-5 w-5" />;
                  colorClass = 'text-blue-600 bg-blue-100';
                  break;
                case 'no_answer':
                  icon = <HiXCircle className="h-5 w-5" />;
                  colorClass = 'text-gray-600 bg-gray-100';
                  break;
                case 'busy':
                  icon = <HiExclamationCircle className="h-5 w-5" />;
                  colorClass = 'text-yellow-600 bg-yellow-100';
                  break;
                case 'failed':
                  icon = <HiXCircle className="h-5 w-5" />;
                  colorClass = 'text-red-600 bg-red-100';
                  break;
                default:
                  icon = <HiPhone className="h-5 w-5" />;
                  colorClass = 'text-gray-600 bg-gray-100';
              }
              
              return (
                <div key={outcome} className={`rounded-lg p-4 ${colorClass.split(' ')[1]}`}>
                  <div className={`flex items-center justify-between mb-2 ${colorClass.split(' ')[0]}`}>
                    {icon}
                    <span className="text-2xl font-bold">{count}</span>
                  </div>
                  <p className="text-sm font-medium capitalize">{outcome.replace('_', ' ')}</p>
                  <p className="text-xs opacity-75">{percentage}%</p>
                </div>
              );
            })}
          </div>
        </div>
      )} */}

          {/* Contacts with Call History */}
          <ContactsWithCalls campaignId={id!} />
        </div>
      ) : activeTab === 'analytics' ? (
        <CampaignAnalytics campaignId={id!} />
      ) : (
        <div className="h-[calc(100vh-220px)]">
          <LiveDataTable campaignId={id!} onViewStateChange={setLiveDataViewParams} />
        </div>
      )}

      {/* Pause Campaign Modal */}
      <ConfirmationModal
        isOpen={showPauseModal}
        onCancel={() => setShowPauseModal(false)}
        onConfirm={() => pauseMutation.mutate()}
        title="Pause Campaign"
        message="Are you sure you want to pause this campaign? Active calls will complete but no new calls will be initiated."
        confirmText="Pause"
        cancelText="Cancel"
      />

      {/* Stop Campaign Modal */}
      <ConfirmationModal
        isOpen={showStopModal}
        onCancel={() => setShowStopModal(false)}
        onConfirm={() => stopMutation.mutate()}
        title="Stop Campaign"
        message="Are you sure you want to stop this campaign? This action cannot be undone and will terminate all active calls."
        confirmText="Stop"
        cancelText="Cancel"
        isDestructive
      />

      {/* Schedule Config Modal */}
      {showScheduleModal && campaign && (
        <ScheduleConfigModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          campaign={campaign}
          onSave={handleScheduleUpdate}
        />
      )}

      <Modal
        isOpen={showExportEmailModal}
        onClose={() => {
          if (!exportMutation.isPending) {
            setShowExportEmailModal(false);
          }
        }}
        title="Download Report"
        size="md"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-[var(--foreground-muted)]">
            Enter an email to receive the report. Leave blank to use your account identity.
          </p>
          <input
            type="email"
            value={exportEmailInput}
            onChange={(e) => setExportEmailInput(e.target.value)}
            placeholder={loggedInIdentity || "name@company.com"}
            className="w-full px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)]"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowExportEmailModal(false)}
              disabled={exportMutation.isPending}
              className="px-4 py-2 rounded-md bg-[var(--secondary)] text-[var(--secondary-foreground)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExportWithEmail}
              disabled={exportMutation.isPending}
              className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
            >
              {exportMutation.isPending ? "Exporting..." : "Send Report"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CampaignDetails; 
